import { useCallback, useEffect, useState } from 'react'
import { Button, Chip, Input, Table, TextArea } from '@heroui/react'
import { api } from '../lib/api'
import {
  callMachine,
  connectorBodyOf,
  connectorDraftOf,
  connectorProblems,
  CONNECTOR_METHODS,
  emptyConnectorDraft,
  outcomeWords,
  secretTooShort,
  toggleMethod,
  type ConnectorDraft,
} from '../lib/connectors'
import { listState } from '../lib/listState'
import { explain } from '../lib/staffErrors'
import { formatMoment } from '../lib/staffText'
import type { Connector, ConnectorCall } from '../lib/types'
import { isPlainHttp } from '../lib/webhooks'
import { Icon } from '../ui/icons'
import { EmptyState, StatusChip } from '../ui/parts'
import { useStaffText } from '../ui/StaffText'
import { Mark, Masked } from './connectParts'
import { Confirm, Toggle, useFlash, useSave } from './sectionKit'
import { IconTile, InnerPage, ListCard, ListRow, ListTable, NameCell, Notice, PageHead, RowArrow, Tick, useOpenRecord } from './settingsKit'
import '../styles/connect.css'

/**
 * Connectors: the addresses an organisation's screen apps may call by name, with
 * the secret each is called with (docs/EXTENSIBILITY.md section 5).
 *
 * The installation owner's (tenant.manage), as webhooks are: a connector is a
 * standing connection to a customer's own system with a credential for it, and
 * the server refuses every route here to anybody else. Shown only when the
 * licence includes screen apps, since a connector is only ever called by one.
 *
 * The secret goes one way. It is typed into a password field that is never
 * prefilled, sealed on the server, and never sent back: a row says only whether
 * one is set. The recent calls, in the connector's drawer, are what the server
 * kept of each call, which is never what was sent or answered.
 */
export function Connectors({ organisation }: { organisation?: string }) {
  const { lang, say } = useStaffText()
  const { flash, toast } = useFlash()
  const [rows, setRows] = useState<Connector[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')

  const load = useCallback(async () => {
    try {
      const { connectors } = await api.connectors(organisation)
      setRows(connectors)
    } catch (err) {
      setError(explain(err, lang, 'common.unreachable'))
    } finally {
      setLoaded(true)
    }
    // lang deliberately not a dependency: a language switch must not reload.
  }, [organisation])

  useEffect(() => {
    void load()
  }, [load])

  // The connector open on its own page (#connectors/<id>, #connectors/new), as the list
  // now has it, so a secret set or cleared from inside it is reflected at once.
  const rec = useOpenRecord(rows, (c) => c.id, loaded)
  const state = listState(loaded, rows.length)

  if ((rec.isNew && loaded) || rec.row) {
    return (
      <>
        <ConnectorDrawer
          key={rec.code}
          connector={rec.row ?? null}
          organisation={organisation}
          flash={flash}
          onClose={rec.close}
          onReload={load}
          onDone={async () => {
            await load()
            rec.close()
          }}
        />
        {toast}
      </>
    )
  }

  return (
    <>
      <PageHead
        title={say('admin.section.connectors')}
        line={say('connect.line.connectors')}
        actions={
          <Button variant="primary" isDisabled={!loaded} onPress={() => rec.open()}>
            <Icon name="plus" size={16} /> {say('connectors.add')}
          </Button>
        }
      />
      {error && <Notice className="connect-gap">{error}</Notice>}

      <ListCard title={say('admin.section.connectors')} count={loaded ? say('connect.connectorCount', { n: rows.length }) : undefined}>
        {state === 'loading' && <p className="empty">{say('common.loading')}</p>}
        {state === 'empty' && <EmptyState icon={<Icon name="plug" size={20} />} title={say('connectors.none')} />}
        {rows.length > 0 && (
          <ListTable
            label={say('admin.section.connectors')}
            cols="minmax(0, 2fr) minmax(0, 1fr) 110px 130px 96px 16px"
            head={[
              say('connect.col.connector'),
              say('connectors.methods'),
              say('connect.col.paths'),
              say('connect.col.secret'),
              say('connect.col.status'),
              '',
            ]}
          >
            {rows.map((c) => (
              <ListRow key={c.id} off={!c.active} label={c.name} onOpen={() => rec.open(c)}>
                <NameCell
                  tile={<IconTile icon="plug" tone={c.active ? 3 : 6} size="sm" />}
                  name={
                    <>
                      <span className="connect-mono">{c.name}</span>
                      {isPlainHttp(c.url) && (
                        <Chip size="sm" variant="soft" color="warning">
                          {say('connect.unencrypted')}
                        </Chip>
                      )}
                    </>
                  }
                  sub={<span className="connect-mono">{c.url}</span>}
                />
                <span className="connect-mono set-muted">{c.methods.join(' ')}</span>
                <span className="set-muted">
                  {c.paths.length > 0 ? say('connect.pathCount', { n: c.paths.length }) : say('connect.addressOnly')}
                </span>
                <span>{c.hasSecret ? <Masked /> : <span className="set-muted">{say('connectors.noSecret')}</span>}</span>
                <span>
                  <StatusChip tone={c.active ? 'ok' : undefined}>{say(c.active ? 'connect.on' : 'connect.off')}</StatusChip>
                </span>
                <RowArrow />
              </ListRow>
            ))}
          </ListTable>
        )}
      </ListCard>

      {toast}
    </>
  )
}

function ConnectorDrawer({
  connector: c,
  organisation,
  flash,
  onClose,
  onReload,
  onDone,
}: {
  /** null for a new connector. */
  connector: Connector | null
  organisation?: string
  flash: (text: string) => void
  onClose: () => void
  /** Reloads the list and leaves the drawer open: after the secret changes. */
  onReload: () => Promise<void>
  /** Reloads the list and closes the drawer. */
  onDone: () => Promise<void>
}) {
  const { lang, say } = useStaffText()
  const noop = useCallback(() => {}, [])
  const { busy, error, save } = useSave(noop, flash)
  const [d, setD] = useState<ConnectorDraft>(c ? connectorDraftOf(c) : emptyConnectorDraft())
  const [tried, setTried] = useState(false)
  const [settingSecret, setSettingSecret] = useState(false)
  const [secret, setSecret] = useState('')
  const problems = connectorProblems(d, lang)
  const set = (patch: Partial<ConnectorDraft>) => setD((x) => ({ ...x, ...patch }))

  async function submit() {
    // Save names every problem, and sends nothing while there is one.
    if (problems.length > 0) {
      setTried(true)
      return
    }
    if (!c) {
      const ok = await save(() => api.createConnector(organisation, connectorBodyOf(d)), say('connect.saved'))
      if (ok) await onDone()
      return
    }
    // An existing connector's secret is set on its own, below, so an edit can
    // never clear it by leaving a field empty.
    const { secret: _, ...body } = connectorBodyOf(d)
    const answer: { secretCleared?: boolean } = {}
    const ok = await save(async () => {
      answer.secretCleared = (await api.updateConnector(c.id, body)).secretCleared
    })
    if (!ok) return
    // The address now names another server, so the server cleared the secret
    // meant for the old one. Short as a toast; the row then says "No secret".
    flash(answer.secretCleared ? say('connect.savedSecretCleared') : say('connect.saved'))
    await onDone()
  }

  async function saveSecret(value: string) {
    if (!c) return
    const ok = await save(
      () => api.setConnectorSecret(c.id, value),
      value === '' ? say('connect.secretClearedShort') : say('connect.secretSavedShort'),
    )
    if (!ok) return
    setSecret('')
    setSettingSecret(false)
    await onReload()
  }

  return (
    <InnerPage
      back={say('admin.section.connectors')}
      title={c ? c.name : say('connectors.add')}
      onClose={onClose}
      onSubmit={() => void submit()}
      submitLabel={c ? say('common.save') : say('connect.addConnector')}
      busy={busy}
      wide={Boolean(c)}
      extra={
        c && (
          <Confirm
            ask={say('common.delete')}
            question={say('connect.deleteConnectorAsk')}
            yes={say('common.delete')}
            keep={say('connect.keep')}
            busy={busy}
            onYes={() =>
              void save(() => api.deleteConnector(c.id), say('connect.deleted')).then((ok) => { if (ok) void onDone() })
            }
          />
        )
      }
    >
      {error && <Notice>{error}</Notice>}
      {tried && problems.length > 0 && <Notice>{problems.join(' ')}</Notice>}

      <label className="field">
        <span title={say('connect.nameRule')}>{say('connectors.name')}</span>
        <Input fullWidth className="connect-mono-input" value={d.name} onChange={(e) => set({ name: e.target.value })} />
      </label>

      <label className="field">
        <span>{say('connectors.url')}</span>
        <Input
          fullWidth
          className="connect-mono-input"
          value={d.url}
          onChange={(e) => set({ url: e.target.value })}
          placeholder={say('connect.egConnectorUrl')}
        />
      </label>
      {isPlainHttp(d.url) && (
        <p className="connect-warn">
          <Icon name="alert" size={15} /> {say('connect.notHttpsSecret')}
        </p>
      )}

      <label className="field">
        <span title={say('connect.pathsRule')}>{say('connectors.paths')}</span>
        <TextArea
          fullWidth
          className="connect-textarea"
          rows={4}
          spellCheck={false}
          value={d.pathsText}
          onChange={(e) => set({ pathsText: e.target.value })}
          placeholder={'/lookup\n/customers/*'}
        />
      </label>
      {/* The Part C review (C-1): anything that reaches a machine's address can
          use its connectors, so the risk is said where the paths are chosen. */}
      <p className="connect-warn">
        <Icon name="alert" size={15} /> {say('connect.exposure')}
      </p>

      <fieldset className="field">
        <legend title={say('connect.firstMethod')}>{say('connectors.methods')}</legend>
        <div className="connect-methods">
          {CONNECTOR_METHODS.map((m) => (
            <Tick key={m} checked={d.methods.includes(m)} onChange={() => set({ methods: toggleMethod(d.methods, m) })}>
              <span className="connect-mono">{m}</span>
            </Tick>
          ))}
        </div>
      </fieldset>

      <div className="connect-two">
        <label className="field">
          <span>{say('connectors.headerName')}</span>
          <Input fullWidth className="connect-mono-input" value={d.headerName} onChange={(e) => set({ headerName: e.target.value })} />
        </label>
        <label className="field">
          <span>{say('connectors.timeout')}</span>
          <Input
            fullWidth
            type="number"
            min={500}
            max={10000}
            step={100}
            value={Number.isFinite(d.timeoutMs) ? d.timeoutMs : ''}
            onChange={(e) => set({ timeoutMs: e.target.valueAsNumber })}
          />
        </label>
      </div>

      {!c && (
        <label className="field">
          <span title={say('connect.secretRule')}>{say('connectors.secretOptional')}</span>
          <Input
            fullWidth
            type="password"
            autoComplete="new-password"
            value={d.secret}
            onChange={(e) => set({ secret: e.target.value })}
          />
        </label>
      )}

      <Toggle title={say('connect.on')} checked={d.active} onChange={(active) => set({ active })} />

      {c && (
        <fieldset className="field connect-group">
          <legend>{say('connectors.secret')}</legend>
          <div className="connect-line">
            <span>
              <strong title={say('connect.secretNeverShown')}>{c.hasSecret ? say('connect.secretSet') : say('connectors.noSecret')}</strong>
              {c.hasSecret && <Masked />}
            </span>
            {!settingSecret && (
              <span className="connect-lineacts">
                {c.hasSecret && (
                  <Confirm
                    ask={say('connect.clear')}
                    question={say('connect.clearSecretAsk')}
                    yes={say('connect.clear')}
                    keep={say('connect.keep')}
                    busy={busy}
                    onYes={() => void saveSecret('')}
                  />
                )}
                <Button variant="secondary" isDisabled={busy} onPress={() => setSettingSecret(true)}>
                  <Mark name="key" size={16} /> {c.hasSecret ? say('connect.replaceSecret') : say('connectors.setSecret')}
                </Button>
              </span>
            )}
          </div>
          {settingSecret && (
            <div className="connect-ask">
              <label className="field">
                <span title={say('connect.secretRule')}>{say('connect.newSecret')}</span>
                <Input
                  fullWidth
                  type="password"
                  autoComplete="new-password"
                  value={secret}
                  onChange={(e) => setSecret(e.target.value)}
                />
              </label>
              <div>
                <Button variant="secondary" isDisabled={busy || secret === '' || secretTooShort(secret)} onPress={() => void saveSecret(secret)}>
                  {say('connect.saveSecret')}
                </Button>
                <Button
                  variant="ghost"
                  onPress={() => {
                    setSecret('')
                    setSettingSecret(false)
                  }}
                >
                  {say('common.cancel')}
                </Button>
              </div>
            </div>
          )}
        </fieldset>
      )}

      {c && <RecentCalls connectorId={c.id} version={c.updatedAt} />}
    </InnerPage>
  )
}

/** The connector's last fifty calls, read when its drawer opens and again after
 *  the connector is changed. */
function RecentCalls({ connectorId, version }: { connectorId: string; version: string }) {
  const { lang, say } = useStaffText()
  const [calls, setCalls] = useState<ConnectorCall[]>([])
  const [loaded, setLoaded] = useState(false)
  const [error, setError] = useState('')

  useEffect(() => {
    let live = true
    api
      .connectorCalls(connectorId)
      .then(({ calls: rows }) => {
        if (!live) return
        setCalls(rows)
        setLoaded(true)
      })
      .catch((err: unknown) => {
        if (live) setError(explain(err, lang, 'common.unreachable'))
      })
    return () => {
      live = false
    }
    // lang deliberately not a dependency: a language switch must not reload.
  }, [connectorId, version])

  return (
    <fieldset className="field connect-group">
      <legend title={say('connectors.recentCallsHint')}>{say('connectors.recentCalls')}</legend>
      {error && <Notice>{error}</Notice>}
      {loaded && calls.length === 0 && <p className="muted">{say('connectors.noCalls')}</p>}
      {calls.length > 0 && (
        <Table variant="secondary" className="connect-calls">
          <Table.ScrollContainer>
            <Table.Content aria-label={say('connectors.recentCalls')}>
              <Table.Header>
                <Table.Column isRowHeader>{say('connectors.col.time')}</Table.Column>
                <Table.Column>{say('connectors.col.machine')}</Table.Column>
                <Table.Column>{say('connectors.col.method')}</Table.Column>
                <Table.Column className="num">{say('connectors.col.status')}</Table.Column>
                <Table.Column>{say('connectors.col.outcome')}</Table.Column>
                <Table.Column className="num">{say('connect.msShort')}</Table.Column>
              </Table.Header>
              <Table.Body>
                {calls.map((call) => (
                  <Table.Row key={call.id} id={call.id}>
                    <Table.Cell>{formatMoment(call.at, lang, { date: true, time: true })}</Table.Cell>
                    <Table.Cell className="connect-mono">{callMachine(call, lang)}</Table.Cell>
                    <Table.Cell className="connect-mono">{call.method}</Table.Cell>
                    <Table.Cell className="num">{call.status === 0 ? say('connectors.noAnswer') : call.status}</Table.Cell>
                    <Table.Cell>{outcomeWords(call.outcome, lang)}</Table.Cell>
                    <Table.Cell className="num">{call.durationMs}</Table.Cell>
                  </Table.Row>
                ))}
              </Table.Body>
            </Table.Content>
          </Table.ScrollContainer>
        </Table>
      )}
    </fieldset>
  )
}
